import React, { useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Cancel, ClearAllOutlined } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { saleService } from "../../services";
import { store } from "../../store";
import { addTab, clearAllTabs, closeTab, setActiveTabByPath } from "../../tabs/tabsSlice";
import {
  privateRoutes,
  sidebarAdminSections,
  sidebarBusinessSections,
} from "../../routes/routes.config";
import { normalizeRoutePath, normalizeTabPath, stripTabSearch } from "../../tabs/tabPath";
import {
  clearAllSalesDrafts,
  removeSalesDraft,
} from "../../state/salesDraftsSlice";
import { getSalesDraftStorageKeyFromPath, isSalesRoutePath } from "../../state/salesDraftUtils";
import {
  collectActiveSaleIdsFromTabs,
  releaseSaleIds,
} from "../sale/saleReservationRelease";

const isHomePath = (path) => {
  const normalized = normalizeRoutePath(path);
  return normalized === "/" || normalized === "/home";
};

const PRODUCT_FORM_DRAFT_STORAGE_PREFIX = "product-form-draft:";

const isProductFormPath = (path) => stripTabSearch(path) === "/products/create";

const getProductFormDraftStorageKeyFromPath = (path) => {
  const normalizedPath = normalizeTabPath(path);
  if (!isProductFormPath(normalizedPath)) return null;
  return `${PRODUCT_FORM_DRAFT_STORAGE_PREFIX}${normalizedPath}`;
};

const clearProductFormDraftByPath = (path) => {
  if (typeof window === "undefined") return;
  const draftKey = getProductFormDraftStorageKeyFromPath(path);
  if (!draftKey) return;
  window.sessionStorage.removeItem(draftKey);
};

const clearAllProductFormDrafts = () => {
  if (typeof window === "undefined") return;
  const keysToRemove = [];
  for (let index = 0; index < window.sessionStorage.length; index += 1) {
    const key = window.sessionStorage.key(index);
    if (String(key || "").startsWith(PRODUCT_FORM_DRAFT_STORAGE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => window.sessionStorage.removeItem(key));
};

const getFallbackLabelFromPath = (path) => {
  if (!path || isHomePath(path)) return "Inicio";
  const lastPart = path.split("/").filter(Boolean).pop() || "Tab";
  return lastPart
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const routePathToRegex = (path) => {
  if (!path || typeof path !== "string") return null;
  const normalized = normalizeRoutePath(path);
  if (normalized.includes("*")) {
    const escaped = escapeRegex(normalized).replace("\\*", ".*");
    return new RegExp(`^${escaped}$`);
  }
  const pattern = normalized
    .split("/")
    .map((segment) => {
      if (!segment) return "";
      if (segment.startsWith(":")) return "[^/]+";
      return escapeRegex(segment);
    })
    .join("/");
  return new RegExp(`^${pattern}$`);
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
};

const buildLabelMatchers = () => {
  const collected = [];

  const pushRoute = (path, label) => {
    const normalizedPath = normalizeRoutePath(path);
    const cleanLabel = String(label || "").trim();
    if (!normalizedPath || !cleanLabel) return;
    if (collected.some((entry) => entry.path === normalizedPath)) return;
    collected.push({
      path: normalizedPath,
      label: cleanLabel,
      regex: routePathToRegex(normalizedPath),
    });
  };

  toArray(privateRoutes).forEach((route) => {
    if (!route || typeof route !== "object") return;
    pushRoute(route.path, route.label);
  });

  [...toArray(sidebarAdminSections), ...toArray(sidebarBusinessSections)].forEach((section) => {
    toArray(section?.items).forEach((item) => {
      pushRoute(item?.path, item?.label);
    });
  });

  return collected;
};

const TAB_LABEL_MATCHERS = buildLabelMatchers();

const resolveTabLabel = (path) => {
  if (isHomePath(path)) return "Inicio";

  const normalizedPath = stripTabSearch(path);
  const exact = TAB_LABEL_MATCHERS.find((entry) => entry.path === normalizedPath);
  if (exact?.label) return exact.label;

  const dynamic = TAB_LABEL_MATCHERS.find((entry) => entry.regex?.test(normalizedPath));
  if (dynamic?.label) return dynamic.label;

  return getFallbackLabelFromPath(normalizedPath);
};

const getSalesTabLabel = (tabPath, draftMap) => {
  const draftKey = getSalesDraftStorageKeyFromPath(tabPath);
  if (!draftKey) return null;
  const draft = draftMap?.[draftKey];
  const customerLabel = String(draft?.saleHeader?.customer || "").trim();
  if (customerLabel) return customerLabel;
  const saleNumber = String(draft?.sale?.sale_number || "").trim();
  if (saleNumber && saleNumber.toUpperCase() !== "BORRADOR") return saleNumber;
  return "Venta Normal";
};

const getNextActivePathOnClose = (tabs, activeTab, closingPath) => {
  const normalizedClosingPath = normalizeTabPath(closingPath);
  if (!normalizedClosingPath) return activeTab;
  if (normalizeTabPath(activeTab) !== normalizedClosingPath) {
    return activeTab;
  }

  const normalizedTabs = tabs.map((tab) => normalizeTabPath(tab.path));
  const closingIndex = normalizedTabs.findIndex((path) => path === normalizedClosingPath);
  const remaining = normalizedTabs.filter((path) => path !== normalizedClosingPath);
  if (!remaining.length) return null;
  const fallbackIndex = closingIndex > 0 ? closingIndex - 1 : 0;
  return remaining[Math.min(fallbackIndex, remaining.length - 1)] || null;
};

const TabItem = React.memo(({ tab, isActive, onClick, onClose, theme, label }) => {
  const handleCloseClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      onClose(event);
    },
    [onClose]
  );

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        px: 1.4,
        py: 0.6,
        borderRadius: "6px",
        backgroundColor: isActive ? theme.palette.primary.main : "transparent",
        color: isActive ? "#fff" : theme.palette.text.primary,
        fontWeight: isActive ? 600 : 500,
        fontSize: "0.85rem",
        cursor: "pointer",
        transition: "all 0.2s ease",
        whiteSpace: "nowrap",
        position: "relative",
        "&:focus, &:focus-visible": {
          outline: "none",
          boxShadow: "none",
        },
        "&:hover": {
          backgroundColor: isActive ? theme.palette.primary.main : theme.palette.action.hover,
        },
        "&::after": !isActive
          ? {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: "20%",
              height: "2px",
              backgroundColor: theme.palette.secondary.main,
              opacity: 0,
              transition: "opacity 0.2s ease, width 0.2s ease",
              borderRadius: "1px",
            }
          : {},
        "&:hover::after": !isActive
          ? { opacity: 0.55, width: "60%" }
          : {},
      }}
    >
      <Typography
        variant="body2"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: 180,
          opacity: isActive ? 1 : 0.9,
        }}
      >
        {label}
      </Typography>

      {tab.closable !== false && (
        <IconButton
          onClick={handleCloseClick}
          tabIndex={-1}
          disableRipple
          size="small"
          sx={{
            ml: 0.75,
            p: "2px",
            color: isActive ? "#fff" : theme.palette.text.secondary,
            opacity: 0.75,
            "&:hover": {
              color: isActive ? "#fff" : theme.palette.error.main,
              opacity: 1,
              backgroundColor: "transparent",
            },
            "&:focus, &:focus-visible, &.Mui-focusVisible": {
              outline: "none",
              boxShadow: "none",
              backgroundColor: "transparent",
            },
          }}
        >
          <Cancel sx={{ fontSize: 15 }} />
        </IconButton>
      )}
    </Box>
  );
});

const CustomTabs = () => {
  const tabs = useSelector((state) => state.tabs.tabs);
  const activeTab = useSelector((state) => state.tabs.activeTab);
  const salesDrafts = useSelector((state) => state.salesDrafts.drafts);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { currentBranch } = useAuth();
  const [closeConfirmDialog, setCloseConfirmDialog] = React.useState({
    open: false,
    tab: null,
    event: null,
  });
  const [closeAllConfirmDialog, setCloseAllConfirmDialog] = React.useState(false);

  const blurActiveElement = useCallback((target) => {
    if (target && typeof target.blur === "function") {
      target.blur();
    }
    if (
      typeof document !== "undefined" &&
      document.activeElement &&
      typeof document.activeElement.blur === "function"
    ) {
      document.activeElement.blur();
    }
  }, []);

  const closeTabAndSyncRoute = useCallback(
    (path) => {
      const closingPath = normalizeTabPath(path);
      if (!closingPath) return;

      const wasClosingActive = normalizeTabPath(activeTab) === closingPath;
      const nextPath = wasClosingActive
        ? getNextActivePathOnClose(tabs, activeTab, closingPath)
        : null;

      dispatch(closeTab(closingPath));

      if (!wasClosingActive) return;

      if (nextPath && !isHomePath(nextPath)) {
        navigate(nextPath, { replace: true });
        return;
      }

      navigate("/", { replace: true });
    },
    [activeTab, dispatch, navigate, tabs]
  );

  useEffect(() => {
    const currentPath = normalizeTabPath(`${location.pathname}${location.search}`) || "/";
    if (isHomePath(currentPath)) {
      dispatch(setActiveTabByPath(null));
      return;
    }
    const exists = tabs.some((tab) => normalizeTabPath(tab.path) === currentPath);
    if (!exists) dispatch(addTab({ path: currentPath, label: resolveTabLabel(currentPath), closable: true }));
    else dispatch(setActiveTabByPath(currentPath));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (activeTab === null) {
      navigate("/", { replace: true });
      return;
    }
    const currentPath = normalizeTabPath(`${location.pathname}${location.search}`);
    if (currentPath !== activeTab) navigate(activeTab, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleTabClick = useCallback(
    (tab, event) => {
      blurActiveElement(event?.currentTarget);
      const path = normalizeTabPath(tab.path);
      if (isHomePath(path)) {
        dispatch(setActiveTabByPath(null));
      } else {
        dispatch(setActiveTabByPath(path));
      }
      navigate(path);
    },
    [dispatch, navigate, blurActiveElement]
  );

  const handleCloseTab = useCallback(
    (tab, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      const closingPath = normalizeTabPath(tab.path);
      if (isSalesRoutePath(closingPath)) {
        const draftKey = getSalesDraftStorageKeyFromPath(closingPath);
        if (draftKey) {
          const draft = store.getState().salesDrafts.drafts[draftKey];
          const saleId = draft?.sale?.id;
          const hasItems = (draft?.sale?.items?.length ?? 0) > 0;

          if (saleId && hasItems) {
            setCloseConfirmDialog({ open: true, tab, event });
            return;
          }

          if (saleId && !hasItems && currentBranch?.id) {
            saleService.deleteSale(saleId, currentBranch.id).catch(() => {});
          }
          if (saleId && hasItems) {
            saleService.releaseReservations({ sale_id: saleId, branch_id: currentBranch?.id }).catch(() => {});
          }
          dispatch(removeSalesDraft(draftKey));
        }
      }
      clearProductFormDraftByPath(closingPath);
      closeTabAndSyncRoute(closingPath);
    },
    [closeTabAndSyncRoute, currentBranch?.id, dispatch]
  );

  const handleCloseAll = useCallback(
    (event) => {
      blurActiveElement(event?.currentTarget);
      const activeSaleIds = collectActiveSaleIdsFromTabs(tabs, salesDrafts);
      if (activeSaleIds.length > 0) {
        setCloseAllConfirmDialog(true);
        return;
      }
      dispatch(clearAllTabs());
      dispatch(clearAllSalesDrafts());
      clearAllProductFormDrafts();
      dispatch(setActiveTabByPath(null));
      navigate("/", { replace: true });
    },
    [blurActiveElement, dispatch, navigate, salesDrafts, tabs]
  );

  const confirmCloseAll = useCallback(async () => {
    const activeSaleIds = collectActiveSaleIdsFromTabs(tabs, salesDrafts);
    await releaseSaleIds(activeSaleIds, currentBranch?.id);
    setCloseAllConfirmDialog(false);
    dispatch(clearAllTabs());
    dispatch(clearAllSalesDrafts());
    clearAllProductFormDrafts();
    dispatch(setActiveTabByPath(null));
    navigate("/", { replace: true });
  }, [currentBranch?.id, dispatch, navigate, salesDrafts, tabs]);

  if (!tabs.length) return null;

  return (
    <>
      <Box sx={{ px: 0, py: 0.5, width: "100%", overflow: "hidden" }}>
        <Box
          display="flex"
          gap={0.5}
          alignItems="center"
          sx={{
            width: "100%",
            overflowX: "auto",
            overflowY: "hidden",
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            px: 0.5,
          }}
        >
          {tabs.map((tab, index) => (
            <motion.div
              key={tab.path}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.15, delay: index * 0.02 }}
              style={{ display: "flex", flexShrink: 0, willChange: "transform" }}
            >
              <TabItem
                tab={tab}
                isActive={normalizeTabPath(activeTab) === normalizeTabPath(tab.path)}
                onClick={(event) => handleTabClick(tab, event)}
                onClose={(event) => handleCloseTab(tab, event)}
                theme={theme}
                label={getSalesTabLabel(tab.path, salesDrafts) || tab.label}
              />
            </motion.div>
          ))}
          <Tooltip title="Cerrar todos">
            <IconButton
              size="small"
              onClick={handleCloseAll}
              sx={{
                flexShrink: 0,
                ml: 0.35,
                border: (currentTheme) => `1px solid ${currentTheme.palette.divider}`,
                borderRadius: 1.25,
                color: "text.secondary",
              }}
            >
              <ClearAllOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Dialog open={closeConfirmDialog.open} maxWidth="xs" fullWidth>
        <DialogTitle>Pedido en gestion</DialogTitle>
        <DialogContent>
          <Typography>
            Este pedido tiene productos en proceso. Si lo cierras se liberaran las cantidades captadas y se
            perdera la venta. Deseas continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseConfirmDialog({ open: false, tab: null, event: null })}>
            Mantener abierta
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              const { tab } = closeConfirmDialog;
              setCloseConfirmDialog({ open: false, tab: null, event: null });
              if (!tab) {
                return;
              }
              const draftKey = getSalesDraftStorageKeyFromPath(tab.path);
              const saleId = store.getState().salesDrafts.drafts[draftKey]?.sale?.id;
              if (saleId) {
                const branchId = currentBranch?.id;
                await saleService.releaseReservations({ sale_id: saleId, branch_id: branchId }).catch(() => {});
              }
              if (draftKey) {
                dispatch(removeSalesDraft(draftKey));
              }
              closeTabAndSyncRoute(tab.path);
            }}
          >
            Cerrar y perder venta
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={closeAllConfirmDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Hay ventas sin finalizar</DialogTitle>
        <DialogContent>
          <Typography>
            Hay ventas sin finalizar. Si cierras, se liberaran los productos captados.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseAllConfirmDialog(false)}>
            Cancelar
          </Button>
          <Button color="error" variant="contained" onClick={confirmCloseAll}>
            Liberar y cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(CustomTabs);
